// Individual, indexable visitor guides for the signature events on the calendar.
// Each guide leads with a direct-answer paragraph (the passage AI engines quote),
// carries {year} / {dates} tokens that are replaced with the live next-occurrence
// from /api/events (falling back to the values below when prerendered/offline),
// and maps ONLY to homes that genuinely fit the trip.
//
// IMPORTANT: content is drafted from the hosts' local knowledge and reviewed by
// the owners before publishing. Keep every claim accurate and current.

export const eventGuides = [
  {
    slug: "stonewall-pride-wilton-manors",
    ogImage: "/og/events/stonewall-pride-wilton-manors.jpg",
    eventId: "stonewall-pride",
    eventName: "Wilton Manors Stonewall Pride Parade & Street Festival",
    shortTitle: "Stonewall Pride Visitor Guide",
    category: "LGBTQ+ & Pride · Wilton Manors",
    h1: "Stonewall Pride Wilton Manors {year} — Parade, Street Festival & Where to Stay",
    metaTitle: "Stonewall Pride Wilton Manors {year} — Parade, Festival & Where to Stay",
    metaDescription:
      "Stonewall Pride Wilton Manors {dates}: the free street festival, the nighttime GLOW Parade, where to eat and park — plus walk-to-it suites a short stroll from Wilton Drive.",
    answer:
      "The Wilton Manors Stonewall Pride Parade & Street Festival takes over Wilton Drive on {dates} — a full day of live stages, food and 100,000+ revellers, capped by the famous nighttime GLOW Parade. The smartest place to stay is within walking distance of the Drive: Double Rainbow Suites' Wilton Manors king suites are a short walk from the parade route, so you skip the road closures and the rideshare surge entirely.",
    fallback: { start: "2027-06-19", end: "2027-06-19", checkout: "2027-06-20", label: "June 19, 2027", year: "2027" },
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    stayNote:
      "All three Wilton Manors homes are a short walk from Wilton Drive — park free, walk in, walk home. The Private Pool Oasis in Fort Lauderdale is about a 5-minute drive from the festival.",
    atGlance: {
      h: "Stonewall Pride Wilton Manors {year} at a Glance",
      items: [
        { icon: "calendar", text: "{dates}" },
        { icon: "pin", text: "Wilton Drive, Wilton Manors" },
        { icon: "clock", text: "Street festival by day — GLOW Parade after dark" },
        { icon: "ticket", text: "Free — festival and parade" },
        { icon: "car", text: "The Drive closes to traffic — walk, don't park" },
        { icon: "house", text: "Double Rainbow Suites: a short walk from the route" },
      ],
    },
    route: {
      h: "The festival & parade layout",
      paragraphs: [
        "Wilton Drive closes to traffic for the day and becomes the festival itself: stages, food stalls, community booths and bars spilling onto the street through the heart of the strip.",
        "The headline moment is the GLOW Parade after dark — an illuminated nighttime parade down the Drive, with every balcony, patio and rooftop along the route packed.",
        "Everything is walkable: once you're on the Drive you're on foot for the day — which is exactly why where you sleep matters so much this weekend.",
      ],
    },
    viewingSpots: {
      h: "Best places to watch the GLOW Parade",
      items: [
        "The north end of the Drive — thinner crowds, same spectacle, and a five-minute walk home to our suites.",
        "A patio table at one of the Drive's bars — claim it early and keep it all evening.",
        "Near Five Points for the parade's most energetic stretch.",
        "Anywhere along the route — arrive before dark; the best rail spots go early.",
      ],
    },
    gettingThere: [
      "Wilton Drive closes to traffic for the festival and nearby parking fills by mid-morning. If you're staying with us in Wilton Manors, leave the car at the house — our king suites are a short walk from the heart of the festival. Coming from elsewhere, rideshare is the least painful option, but expect surge pricing when the GLOW Parade ends.",
      "Drivers should aim for the municipal lots on the side streets off NE 26th Street before mid-morning — or book one of our homes, park free, and stroll over.",
      "Flying in for Pride: FLL airport is about 15 minutes away, and all weekend the Drive is a pedestrian zone — comfortable shoes beat a perfect parking spot every time.",
    ],
    restaurants: {
      h: "Where to eat & drink on the Drive",
      items: [
        "Rosie's Bar & Grill — the patio institution; burgers and brunch with a front-row seat.",
        "Georgie's Alibi Monkey Bar — the classic, packed wall-to-wall on Pride night.",
        "Hunters — dance floor and balcony overlooking the route.",
        "Tropics — piano bar and supper club, a Wilton Manors original.",
        "The Eagle — the leather-and-levi institution at the north end, near our suites.",
      ],
    },
    whyStay: {
      h: "Why stay a short walk from the Drive",
      text: "Pride weekend has two pinch points: getting in (Wilton Drive closes to traffic and the lots fill by mid-morning) and getting out (thousands of people ordering rideshares the moment the GLOW Parade ends). Staying a short walk away erases both — park free at your home, stroll to the festival, dance, and walk home in minutes while the surge pricing rages. Our three Wilton Manors homes at 1425 NE 24th St were made for exactly this weekend.",
    },
    weekendExtras: {
      h: "Make a weekend of it",
      items: [
        "Friday: the Drive warms up early — every bar runs its own pre-Pride party.",
        "Saturday: the street festival by day, the GLOW Parade after dark — the one-two punch.",
        "Sunday: the legendary drag brunch at Rosie's, then a lazy beach afternoon.",
        "Recovery move: Fort Lauderdale Beach is about 10 minutes away — salt water fixes everything.",
      ],
    },
    knowBefore: [
      "The street festival runs through the afternoon; the GLOW Parade steps off after dark — that's the headline moment.",
      "June in South Florida is hot and humid: sunscreen, a refillable water bottle and a plan to pace yourself — the after-parties run late.",
      "The festival is free to attend; bring a card for food stalls, drinks and the bars along the Drive.",
      "Every bar on Wilton Drive throws its own Pride party — Hunters, Georgie's Alibi and The Eagle are the classics.",
    ],
    hostTip:
      "Watch the GLOW Parade from the north end of the Drive near our suites — thinner crowds, same spectacle — then walk home in five minutes while everyone else queues for an Uber.",
    faqs: [
      {
        q: "When is Stonewall Pride Wilton Manors {year}?",
        a: "The {year} festival takes over Wilton Drive on {dates}. The street festival runs through the day and the nighttime GLOW Parade is the finale.",
      },
      {
        q: "What is Stonewall Pride Wilton Manors?",
        a: "Wilton Manors' Pride celebration: a free daytime street festival on Wilton Drive with live stages, food and community booths, capped by the illuminated nighttime GLOW Parade — one of the biggest Pride events in Florida.",
      },
      {
        q: "What is the GLOW Parade?",
        a: "The festival's after-dark finale: an illuminated night parade down Wilton Drive — the spectacle the whole weekend builds toward. Claim your spot on the Drive before dark.",
      },
      {
        q: "Is Stonewall Pride Wilton Manors free?",
        a: "Yes — the street festival and the GLOW Parade are free. Bring budget for food, drinks and the after-parties along the Drive.",
      },
      {
        q: "Where do I park for Wilton Manors Pride?",
        a: "Parking is extremely limited once Wilton Drive closes. Guests at our Wilton Manors homes park free at the property and walk; otherwise arrive early for the municipal lots or use rideshare.",
      },
      {
        q: "Where should I stay for Stonewall Pride Wilton Manors?",
        a: "Within walking distance of Wilton Drive — road closures make driving impractical. Our Wilton Manors king suites are a short walk from the parade route, pet-friendly, and bookable direct.",
      },
      {
        q: "How far are Double Rainbow Suites from the festival?",
        a: "Our three Wilton Manors homes at 1425 NE 24th St are a short walk from the north end of Wilton Drive; the Private Pool Oasis in Fort Lauderdale is about a 5-minute drive away.",
      },
      {
        q: "Which Double Rainbow Suites home is best for Pride?",
        a: "Couples: the two king suites — walk home in minutes. Friends sharing: the 2BR efficiencies sleep four, right by the Drive. Want a pool between parties? The Private Pool Oasis sleeps six, five minutes away.",
      },
      {
        q: "What should I wear and bring to Stonewall Pride?",
        a: "June heat is real: sunscreen, a refillable water bottle, comfortable shoes — and something that glows for the night parade. Bring a card for food stalls, drinks and the bars.",
      },
      {
        q: "What else is there to do during Pride weekend in Wilton Manors?",
        a: "Every bar on the Drive throws its own party all weekend — Hunters, Georgie's Alibi and The Eagle are the classics — Sunday brings the famous drag brunch at Rosie's, and Fort Lauderdale Beach is 10 minutes away for the recovery day.",
      },
    ],
    officialUrl: "https://www.stonewallpride.lgbt/",
  },
  {
    slug: "fort-lauderdale-international-boat-show",
    ogImage: "/og/events/fort-lauderdale-international-boat-show.jpg",
    eventId: "flibs",
    eventName: "Fort Lauderdale International Boat Show",
    shortTitle: "Boat Show (FLIBS) Guide",
    category: "Boating · Fort Lauderdale",
    h1: "Fort Lauderdale International Boat Show {year} — Tickets, Venues, Parking & Where to Stay",
    metaTitle: "Fort Lauderdale International Boat Show {year} — Tickets, Venues, Parking & Where to Stay",
    metaDescription:
      "Fort Lauderdale International Boat Show (FLIBS), {dates}: the venues, tickets, Water Taxi and parking tips, nearby restaurants and where to stay — a local guide from hosts 15 minutes away.",
    answer:
      "The Fort Lauderdale International Boat Show (FLIBS) — the world's largest in-water boat show — runs {dates} across Bahia Mar and a string of waterfront marinas linked by shuttle and Water Taxi. Double Rainbow Suites' pet-friendly homes are about 15 minutes from the docks, with free parking, full kitchens and quiet nights — a calmer, better-value base than the sold-out beachfront hotels.",
    fallback: { start: "2026-10-28", end: "2026-11-01", checkout: "2026-11-02", label: "October 28 – November 1, 2026", year: "2026" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    stayNote:
      "All four homes are roughly 15 minutes from Bahia Mar with free parking — gold during show week. Groups and crews pick the Private Pool Oasis (sleeps six); couples pick the king suites a short walk from dinner on Wilton Drive; the 2BR efficiencies sleep four.",
    atGlance: {
      h: "Fort Lauderdale International Boat Show {year} at a Glance",
      items: [
        { icon: "calendar", text: "{dates}" },
        { icon: "pin", text: "Bahia Mar, Fort Lauderdale Beach + satellite marinas" },
        { icon: "clock", text: "Five days, Wednesday through Sunday" },
        { icon: "ticket", text: "Ticketed — buy online ahead; multi-day passes are best value" },
        { icon: "car", text: "No parking at the show — official shuttles & Water Taxi" },
        { icon: "house", text: "Double Rainbow Suites: about 15 minutes from the docks" },
      ],
    },
    route: {
      h: "The show venues",
      paragraphs: [
        "FLIBS isn't one hall — it's a chain of waterfront marinas turned show floors, anchored by the Bahia Mar Yachting Center on Fort Lauderdale Beach, where the superyacht row and the biggest displays sit.",
        "Satellite venues spread up the waterway: the Superyacht Village at Pier Sixty-Six Marina, the Las Olas Marina, the Hall of Fame Marina and the 17th Street Yacht Basin each host their own sections — from center consoles and fishing boats to marine tech and tenders.",
        "You don't drive between them: official show shuttles and the Water Taxi link every venue, and a show ticket covers the hops. First-timers should start at Bahia Mar and work north.",
      ],
    },
    viewingSpots: {
      h: "What not to miss",
      items: [
        "Superyacht row at Bahia Mar — the headline sight and the postcard shot.",
        "Superyacht Village at Pier Sixty-Six — the biggest vessels, across the 17th Street Causeway.",
        "The floating docks of center consoles and fishing boats — step aboard and compare.",
        "Seminars and fishing clinics run through the day — check the official schedule on the morning of your visit.",
        "A weekday morning if you can swing it — same boats, a fraction of the crowd.",
      ],
    },
    gettingThere: [
      "Don't plan on parking at the show: official show parking sits off-site with shuttle service, and the Water Taxi is the local way to arrive — it stops at the venues and the ride is part of the fun. Rideshare works too, with surge pricing at closing time.",
      "Coming from Miami or West Palm Beach, Brightline to downtown Fort Lauderdale plus the Water Taxi beats I-95 entirely.",
      "From our homes it's about 15 minutes by car to the show zone. Our guests' move: drive in before mid-morning or rideshare to a Water Taxi stop and float in.",
    ],
    restaurants: {
      h: "Restaurants near the show",
      items: [
        "15th Street Fisheries — dockside seafood just off the 17th Street Causeway, minutes from Bahia Mar.",
        "Coconuts — Intracoastal-front favorite by the 17th Street bridge; expect a wait on show nights.",
        "Boathouse at the Riverside — on the New River, handy for the Las Olas Marina venue.",
        "The marina decks around Bahia Mar — quick bites between the docks without leaving the show.",
        "Our pick: dinner on Wilton Drive after the docks close — 15 minutes away, a world away from the crowds.",
      ],
    },
    whyStay: {
      h: "Why stay in Wilton Manors",
      text: "Show week is one of the busiest of the year in Fort Lauderdale — beachfront hotels spike in price and sell out months ahead, and parking anywhere near the docks is a daily battle. Our homes sit about 15 minutes away in Wilton Manors: free parking at your door, a full kitchen instead of room service, quiet nights, and Wilton Drive's restaurants and bars a short walk away for the evenings. It's the base the boat-show regulars wish they'd found first.",
    },
    weekendExtras: {
      h: "Make a week of it",
      items: [
        "Take a weekday morning for the show itself — the same boats with room to breathe.",
        "Halloween falls right in the heart of show week most years — and Wicked Manors, Wilton Drive's legendary Halloween street party, is 15 minutes from the docks.",
        "A Water Taxi day pass on a non-show day — ride the waterway you spent the week admiring.",
        "Fort Lauderdale Beach is next door to Bahia Mar — sunrise walk before the gates open.",
        "Sunday: a long brunch on Wilton Drive — Rosie's is the local institution.",
      ],
    },
    knowBefore: [
      "The show runs five days and covers superyachts, center consoles and marine tech — pick a weekday if you can; weekends are packed.",
      "Wear boat-friendly shoes (many docks ask for soft soles) and bring sun protection — most of the show is outdoors.",
      "Tickets are sold online in advance; multi-day passes are the best value if you're seriously shopping.",
      "Book accommodation early — boat-show week is one of the busiest of the year in Fort Lauderdale.",
    ],
    hostTip:
      "Do the show by day, then retreat to Wilton Manors for dinner — the Drive's restaurants are 15 minutes from the docks and a world away from the show crowds.",
    faqs: [
      {
        q: "When is the Fort Lauderdale International Boat Show {year}?",
        a: "FLIBS {year} runs {dates}, anchored by Bahia Mar on Fort Lauderdale Beach with satellite marina venues up the waterway.",
      },
      {
        q: "Where does the Fort Lauderdale Boat Show take place?",
        a: "Across a chain of waterfront marinas: Bahia Mar Yachting Center is the main hub, with the Superyacht Village at Pier Sixty-Six, Las Olas Marina, Hall of Fame Marina and the 17th Street Yacht Basin — all linked by official shuttles and the Water Taxi.",
      },
      {
        q: "How much are tickets to the Fort Lauderdale Boat Show?",
        a: "FLIBS is ticketed, with single-day and multi-day passes sold online in advance through the official site — multi-day passes are the best value if you're seriously shopping. Check flibs.com for this year's pricing.",
      },
      {
        q: "How do I get between boat show venues?",
        a: "Official show shuttles and the Water Taxi connect every venue, and your show ticket covers the hops. Start at Bahia Mar and work north — driving between venues is the slow option.",
      },
      {
        q: "Where can I park for the Fort Lauderdale Boat Show?",
        a: "There's no general parking at the show itself — official parking is off-site with shuttles, and the Water Taxi is the smarter arrival. Staying with us, you park free at your home and drive or rideshare 15 minutes in.",
      },
      {
        q: "What restaurants are near the Fort Lauderdale Boat Show?",
        a: "Near the docks: 15th Street Fisheries and Coconuts by the 17th Street Causeway, Boathouse at the Riverside on the New River, and the marina decks at Bahia Mar for quick bites. For dinner, Wilton Drive is 15 minutes away and a world away from the crowds.",
      },
      {
        q: "Where should I stay for the Fort Lauderdale Boat Show?",
        a: "Close, but not on the beach — show-week beachfront hotels spike and sell out. Our pet-friendly homes are about 15 minutes from Bahia Mar with free parking, full kitchens and walkable dinners on Wilton Drive.",
      },
      {
        q: "How far is Double Rainbow Suites from the boat show?",
        a: "About 15 minutes by car to Bahia Mar and the show zone, or a short rideshare to a Water Taxi stop. Easy in the morning, easy home at night.",
      },
      {
        q: "Which Double Rainbow Suites home is best for the boat show?",
        a: "Crews and groups pick the Private Pool Oasis — sleeps six with a heated pool for the evenings. Couples love the two Wilton Manors king suites a short walk from the Drive, and the spacious 2BR efficiencies sleep four.",
      },
      {
        q: "What else is there to do during boat show week?",
        a: "Fort Lauderdale Beach is next door to the show, a Water Taxi day pass turns the waterway into a sightseeing loop, and Halloween often lands mid-week — Wicked Manors on Wilton Drive is one of the biggest Halloween street parties in Florida.",
      },
    ],
    officialUrl: "https://www.flibs.com/",
  },
  {
    slug: "tortuga-music-festival",
    ogImage: "/og/events/tortuga-music-festival.jpg",
    eventId: "tortuga",
    eventName: "Tortuga Music Festival",
    shortTitle: "Tortuga Music Festival Guide",
    category: "Music · Fort Lauderdale Beach",
    h1: "Tortuga Music Festival {year} — Tickets, Getting There & Where to Stay",
    metaTitle: "Tortuga Music Festival {year} — Tickets, Getting There & Where to Stay",
    metaDescription:
      "Tortuga Music Festival {dates} on Fort Lauderdale Beach: tickets, shuttle and bike tips (no festival parking), restaurants nearby and where to stay — from hosts 10 minutes away.",
    answer:
      "Tortuga Music Festival brings three days of country, rock and pop to the sand at Fort Lauderdale Beach Park on {dates}. Double Rainbow Suites' homes are about a 10-minute drive from the festival gates — including a private-pool home that sleeps six, ideal for a group splitting a festival weekend — with free parking and self check-in for late nights.",
    fallback: { start: "2027-04-02", end: "2027-04-04", checkout: "2027-04-05", label: "April 2–4, 2027", year: "2027" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    stayNote:
      "The Private Pool Oasis sleeps six and is the group favorite; our Wilton Manors suites suit couples who want the festival by day and the Drive by night. Every home has free parking, a kitchen and self check-in for late nights.",
    atGlance: {
      h: "Tortuga Music Festival {year} at a Glance",
      items: [
        { icon: "calendar", text: "{dates}" },
        { icon: "pin", text: "Fort Lauderdale Beach Park, right on the sand" },
        { icon: "clock", text: "Gates around midday — headliners close each night" },
        { icon: "ticket", text: "Ticketed — 3-day and single-day passes; it sells out" },
        { icon: "car", text: "No festival parking — shuttles, Water Taxi, rideshare or bike" },
        { icon: "house", text: "Double Rainbow Suites: about 10 minutes from the gates" },
      ],
    },
    route: {
      h: "The festival layout",
      paragraphs: [
        "Tortuga takes over Fort Lauderdale Beach Park, right on the sand — stages facing the Atlantic, with the ocean as the backdrop and the festival gates on the beach side.",
        "Between the stages sits the Conservation Village — the festival's ocean-conservation heart — plus food rows, bars and shade structures. Comfortable shoes and a hat are not optional.",
        "Gates open around midday and the headliners close each night: three days, one beach, and about 10 minutes from our homes to the gates.",
      ],
    },
    viewingSpots: {
      h: "Festival day tips",
      items: [
        "Near the soundboard for the cleanest mix — the classic move.",
        "Stage-hop early for the discovery acts; the headliners you'll hear from anywhere on the sand.",
        "Back-of-field for space and shorter bar lines; up front for the big closers.",
        "The Conservation Village between sets — free, and the reason the festival exists.",
        "Sunset sets are the Tortuga magic hour — be on the sand for at least one.",
      ],
    },
    gettingThere: [
      "There is no general festival parking at Fort Lauderdale Beach Park — plan on the official shuttles, the Water Taxi, rideshare or a bike. From our homes it's about 10 minutes by car, and the complimentary beach-cruiser bikes make the ride an easy, sunny 20 minutes.",
      "Rideshare drop-off zones sit near the park; expect a walk and surge pricing after the headliner. Locals bike or shuttle — it's genuinely faster at peak times.",
      "Coming from Miami or West Palm Beach? Brightline to downtown Fort Lauderdale plus a quick rideshare or Water Taxi hop beats the beach traffic entirely.",
    ],
    restaurants: {
      h: "Restaurants near the festival",
      items: [
        "The A1A strip by the park — casual beach cafés built for festival crowds.",
        "Coconuts — dockside seafood on the Intracoastal, just off the 17th Street Causeway.",
        "15th Street Fisheries — a classic waterfront spot minutes from the gates.",
        "Las Olas Boulevard — a short rideshare when you want a proper sit-down dinner.",
        "Our pick: late dinner on Wilton Drive after the headliner — 10 minutes home, no beach exodus.",
      ],
    },
    whyStay: {
      h: "Why stay 10 minutes from the gates",
      text: "Tortuga has no festival parking, and the beach hotels triple their rates for the weekend. The winning setup: a home with free parking and a kitchen 10 minutes from the gates, complimentary beach-cruiser bikes for the sunny ride in, and — for groups — a private heated pool waiting when the last set ends. Couples take the Wilton Manors king suites with the Drive's nightlife on foot; friends split the Private Pool Oasis.",
    },
    weekendExtras: {
      h: "Make a long weekend of it",
      items: [
        "Thursday: arrive, stock the kitchen, dinner on Wilton Drive — pace yourself.",
        "Festival days: bikes to the beach, Conservation Village between sets, sunset at the main stage.",
        "Monday: the crowds fly home — claim the quiet beach morning they all miss.",
        "Any nightcap: Wilton Drive's bars stay humming long after the headliners end.",
      ],
    },
    knowBefore: [
      "Tortuga sells 3-day and single-day passes and regularly sells out — buy direct from the official festival site.",
      "It's on the beach: sunscreen, a hat and a small bag. Check the festival's current bag policy before you go.",
      "April weather is close to perfect, but the sand reflects the sun — hydrate between sets.",
      "The festival supports ocean conservation — the Conservation Village between stages is worth a wander.",
    ],
    hostTip:
      "Book the Private Pool Oasis with friends — festival days end better with a night swim and a kitchen for midnight tacos, ten minutes from the gates.",
    faqs: [
      {
        q: "When is Tortuga Music Festival {year}?",
        a: "Tortuga {year} runs {dates} at Fort Lauderdale Beach Park, right on the sand.",
      },
      {
        q: "Where is Tortuga Music Festival held?",
        a: "At Fort Lauderdale Beach Park on A1A, with stages facing the Atlantic — about 10 minutes from our homes.",
      },
      {
        q: "How much are Tortuga Music Festival tickets?",
        a: "3-day and single-day passes are sold through the official festival site, and the festival regularly sells out — buy direct and early.",
      },
      {
        q: "Can you park at Tortuga Music Festival?",
        a: "There's no general festival parking at Fort Lauderdale Beach Park. Use the official shuttles, Water Taxi, rideshare — or our complimentary beach-cruiser bikes.",
      },
      {
        q: "How do I get to Tortuga Music Festival?",
        a: "Official shuttles, the Water Taxi, rideshare or bike — from our homes it's 10 minutes by car or a sunny 20 minutes on the free cruiser bikes. Brightline plus a rideshare works well from Miami or West Palm.",
      },
      {
        q: "What restaurants are near Tortuga Music Festival?",
        a: "The A1A strip by the park for casual bites, Coconuts and 15th Street Fisheries just off the 17th Street Causeway, Las Olas for a sit-down dinner — or late dinner on Wilton Drive, 10 minutes from our homes.",
      },
      {
        q: "Where should I stay for Tortuga Music Festival?",
        a: "About 10 minutes from the gates: our Private Pool Oasis sleeps six (great for groups) and our Wilton Manors king suites are ideal for couples. All are pet-friendly with free parking.",
      },
      {
        q: "Which Double Rainbow Suites home is best for Tortuga?",
        a: "The Private Pool Oasis for groups — sleeps six, heated pool for the post-festival cooldown. Couples love the king suites with Wilton Drive on foot; the 2BR efficiencies sleep four.",
      },
      {
        q: "What should I bring to Tortuga?",
        a: "Sunscreen, a hat, a refillable water bottle and a small bag (check the festival's current bag policy). April sun on reflective sand is stronger than it feels.",
      },
      {
        q: "What else is there to do during Tortuga weekend?",
        a: "The beach itself, a Water Taxi ride along the Intracoastal, Las Olas Boulevard, and Wilton Drive's restaurants and famous drag brunch — all minutes from our homes.",
      },
    ],
    officialUrl: "https://tortugamusicfestival.com/",
  },
  {
    slug: "fort-lauderdale-air-show",
    ogImage: "/og/events/fort-lauderdale-air-show.jpg",
    eventId: "air-show",
    eventName: "Fort Lauderdale Air Show",
    shortTitle: "Fort Lauderdale Air Show Guide",
    category: "Air & Sea · Fort Lauderdale Beach",
    h1: "Fort Lauderdale Air Show {year} — Where to Watch, Stay & Parking Guide",
    metaTitle: "Fort Lauderdale Air Show {year} — Where to Watch, Stay & Parking Guide",
    metaDescription:
      "Fort Lauderdale Air Show {dates}: the schedule, best free beach viewing spots, parking and bike tips, nearby restaurants and where to stay — a local guide from hosts 10 minutes away.",
    answer:
      "The Fort Lauderdale Air Show returns to Fort Lauderdale Beach on {dates}, bringing military jet teams, aerobatics and high-speed demonstrations to the skies over the Atlantic. The show is centered around Fort Lauderdale Beach near Sunrise Boulevard and A1A, with public viewing available from the beach.\n\nStaying at Double Rainbow Suites puts you about 10 minutes from Fort Lauderdale Beach, with complimentary beach-cruiser bikes that give you another way to get to the show without dealing with the worst of the beach parking and traffic.",
    fallback: { start: "2027-05-15", end: "2027-05-16", checkout: "2027-05-17", label: "May 15–16, 2027", year: "2027" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    photos: [
      {
        src: "photo-1503348379917-758650634df4",
        alt: "Five jets flying in formation with smoke trails at an air show",
        caption: "Formation teams run the same line both days — pick your forecast",
      },
      {
        src: "photo-1484199383121-dfa3c30608cd",
        alt: "Aerial view of Fort Lauderdale Beach with crowds on the sand",
        caption: "The beach becomes the grandstand — free to watch for miles",
      },
      {
        src: "photo-1573220785540-c19c0d57905d",
        alt: "Jets releasing colored smoke trails during an air show display",
        caption: "Smoke trails over the Atlantic, ten minutes from your suite",
      },
    ],
    stayNote:
      "We host air-show weekends every year, and here's what we'd tell a friend: stay about 10 minutes from the sand, not on it. All four of our homes fit the bill — groups take the Private Pool Oasis (sleeps six, with the heated pool waiting after a day in the sun), couples take the Wilton Manors king suites, and the 2BR efficiencies sleep four. Free parking, full kitchens, and cruiser bikes that get you to the beach in 20 sunny minutes.",
    atGlance: {
      h: "{year} Fort Lauderdale Air Show at a Glance",
      items: [
        { icon: "calendar", text: "{dates}" },
        { icon: "pin", text: "Fort Lauderdale Beach — near Sunrise Boulevard & A1A" },
        { icon: "airplane", text: "Military jets, aerobatics & air demonstrations" },
        { icon: "ticket", text: "Public beach viewing is free" },
        { icon: "car", text: "Beach parking fills early and traffic can be heavy" },
        { icon: "bicycle", text: "Double Rainbow Suites guests can use complimentary beach-cruiser bikes" },
        { icon: "house", text: "Double Rainbow Suites: about 10 minutes from the beach" },
      ],
    },
    route: {
      h: "The show zone & schedule",
      paragraphs: [
        "'Show center' is the beachfront at Sunrise Boulevard and A1A — but the flying is visible for miles up and down the sand, so you never need to be at the epicenter to see the jets.",
        "Displays run roughly midday to late afternoon on both days: military demonstration teams, parachute jumps and aerobatic pilots over the Atlantic, with the loudest passes right over show center.",
        "Locals still call it the Air & Sea Show — the event's longtime name — and you'll see both names around town. Both days usually fly a similar program, so pick the day with the better forecast; May showers pass quickly and the flying usually carries on between them.",
      ],
    },
    viewingGuide: {
      h: "Where Is the Best Place to Watch the Fort Lauderdale Air Show?",
      paragraphs: [
        "Fort Lauderdale Beach is the main place to watch the Fort Lauderdale Air Show, with the demonstrations taking place over the Atlantic Ocean along the beachfront near Sunrise Boulevard and A1A.",
        "For the biggest event atmosphere, head toward the main show area around Fort Lauderdale Beach Park and Sunrise Boulevard. This is where you'll find some of the largest crowds and easiest access to the heart of the event.",
        "If you'd rather have a little more space, consider finding a spot farther south along Fort Lauderdale Beach. You'll still have a great view of the aircraft overhead while getting farther away from the densest crowds.",
        "DRS tip: If you're staying at Double Rainbow Suites, you don't necessarily need to fight for a parking space near the main show area. Our complimentary beach-cruiser bikes give guests another option for getting to the beach and avoiding some of the Air Show weekend traffic.",
      ],
      sub: {
        h: "What Is the Best Part of the Beach to Watch From?",
        paragraphs: [
          "For first-time visitors, the central Fort Lauderdale Beach area near Sunrise Boulevard is the best choice if you want to be close to the action. For a somewhat less crowded experience, look farther south along the beach.",
          "The Air Show covers a broad section of the Fort Lauderdale beachfront, so you don't have to be directly in front of the main event area to see the aircraft.",
        ],
      },
    },
    gettingThere: [
      "Show center is the beachfront at Sunrise Boulevard and A1A. Beach parking lots fill before mid-morning on show days: arrive early, rideshare, or do what our guests do and take the complimentary beach-cruiser bikes — about 20 easy, sunny minutes from our homes.",
      "Roads around the beach are heavily congested on show days. If you drive, park west of the Intracoastal and walk or Water Taxi across. Coming from Miami or West Palm Beach, Brightline to downtown Fort Lauderdale plus a quick rideshare beats the traffic entirely.",
      "Expect a slow exit when the last jet lands — we send guests back over the bridge for dinner on Wilton Drive while the traffic untangles.",
    ],
    restaurants: {
      h: "Restaurants near the show",
      items: [
        "Casablanca Café — a beachfront favorite in a historic house on A1A, close to show center.",
        "The A1A beach strip — casual cafés and bars that handle the show crowds all weekend.",
        "Coconuts — dockside seafood on the Intracoastal, an easy stop on the way home.",
        "Hugh Taylor Birch State Park sits right across A1A — pack a cooler and picnic on the park side.",
        "Our pick: dinner back on Wilton Drive after the show — a short walk from your suite, no beach traffic.",
      ],
    },
    whyStay: {
      h: "Why stay in Wilton Manors",
      text: "An air-show weekend has one rule: stay close enough to reach the beach easily, far enough to escape it. Our Wilton Manors homes are about 10 minutes from the sand — near enough to pop back for a midday break from the sun and noise — with free parking, full kitchens and a walkable dining strip for the evenings. Beachfront hotels charge peak rates for a weekend you'll spend mostly outdoors; our guests ride the free cruiser bikes in and put the savings toward dinner.",
    },
    weekendExtras: {
      h: "Make a weekend of it",
      items: [
        "Friday: arrive, settle in, dinner and a wander on Wilton Drive — the flying starts around midday Saturday.",
        "Saturday evening: the beach empties fast after the last pass — golden hour on the promenade, then dinner back over the bridge.",
        "Sunday morning: coffee and a stroll through Hugh Taylor Birch State Park, just across A1A from show center.",
        "Sunday night: the Drive's bars and restaurants at full weekend hum — all a short walk from your suite.",
      ],
    },
    knowBefore: [
      "The show is free to watch from the public beach; premium viewing areas with seating are sold on the official site.",
      "Jets are loud — bring ear protection for little ones, and let nervous pets nap at your (pet-friendly) home instead.",
      "Afternoon showers are possible in May; the flying usually carries on between them.",
      "Bring a beach setup — chairs, umbrella, cooler. The flying runs roughly midday to late afternoon.",
    ],
    hostTip:
      "We send guests to the quieter sand just south of show center — same jets, half the crowd — and you're back at the pool while the traffic is still crawling on A1A.",
    faqs: [
      {
        q: "When is the Fort Lauderdale Air & Sea Show {year}?",
        a: "The {year} show flies over Fort Lauderdale Beach on {dates}, with displays running roughly midday to late afternoon, centered near Sunrise Boulevard and A1A.",
      },
      {
        q: "Is the Air & Sea Show the same as the Fort Lauderdale Air Show?",
        a: "Yes — Air & Sea Show is the event's longtime local name; today it's officially billed as the Fort Lauderdale Air Show. Same beach, same jets.",
      },
      {
        q: "Is the Fort Lauderdale Air Show free?",
        a: "Yes — watching from the public beach is free, and the flying is visible for miles. Premium seating areas are ticketed through the official site.",
      },
      {
        q: "Where is the best place to watch the Air & Sea Show?",
        a: "Anywhere on the public sand works — our favorite is the quieter stretch just south of show center at Sunrise & A1A: same jets, half the crowd. Arrive before midday for the best spots.",
      },
      {
        q: "Where should I park for the Fort Lauderdale Air Show?",
        a: "Beach lots fill before mid-morning and roads are congested. Our guests take the complimentary beach-cruiser bikes; otherwise park west of the Intracoastal and walk or Water Taxi over.",
      },
      {
        q: "What restaurants are near the Fort Lauderdale Air Show?",
        a: "Casablanca Café on A1A is close to show center, the beach strip handles the crowds all weekend, and Coconuts on the Intracoastal is an easy stop heading home. Our pick: dinner on Wilton Drive after the show, a short walk from our homes.",
      },
      {
        q: "Where should I stay for the Air & Sea Show?",
        a: "About 10 minutes from the beach is the sweet spot — close enough to bike in, far enough to escape the gridlock and the beachfront price spike. All of our pet-friendly homes fit, including a private-pool home that sleeps six.",
      },
      {
        q: "How far is Double Rainbow Suites from the air show?",
        a: "About 10 minutes by car, or roughly 20 easy minutes on the complimentary beach-cruiser bikes — our guests' favorite way to skip the parking crunch.",
      },
      {
        q: "Which Double Rainbow Suites home is best for the air show?",
        a: "Groups pick the Private Pool Oasis — sleeps six, with the pool waiting for the post-beach cooldown. Couples love the two Wilton Manors king suites, and the spacious 2BR efficiencies sleep four.",
      },
      {
        q: "What else is there to do in Fort Lauderdale that weekend?",
        a: "Hugh Taylor Birch State Park sits right across from show center, the promenade runs for miles, the Water Taxi hops along the Intracoastal, and Wilton Drive's restaurants and famous drag brunch are a short walk from our homes.",
      },
    ],
    officialUrl: "https://air.show/fortlauderdale/",
  },
  {
    slug: "winterfest-boat-parade",
    ogImage: "/og/events/winterfest-boat-parade.jpg",
    eventId: "winterfest",
    eventName: "Winterfest Boat Parade",
    shortTitle: "Winterfest Boat Parade Guide",
    category: "Holiday · Fort Lauderdale",
    h1: "Fort Lauderdale Winterfest Boat Parade {year} — Where to Stay, Viewing, Parking & Local Guide",
    metaTitle: "Fort Lauderdale Christmas Boat Parade {year}: Where to Watch",
    metaDescription:
      "Find where to watch the {dates} Fort Lauderdale Winterfest Boat Parade, key viewing areas, parking tips, and nearby places to stay in Wilton Manors.",
    answer:
      "The Winterfest Boat Parade — Fort Lauderdale's famous Christmas boat parade, the 'Greatest Show on H2O' — lights up the city on {dates}: a mile-long procession of lavishly illuminated yachts cruising a 12-mile route from the New River downtown up the Intracoastal Waterway to Pompano Beach, with spectators lining the water the whole way. Watching is free from dozens of waterfront spots, and Double Rainbow Suites' pet-friendly homes sit about 10–15 minutes from the best mid-route viewing — close enough for an easy rideshare, far enough to sleep in.",
    fallback: { start: "2026-12-12", end: "2026-12-12", checkout: "2026-12-13", label: "December 12, 2026", year: "2026" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    photos: [
      {
        src: "photo-1503198383638-2199ff0637a3",
        alt: "Aerial view of boats along Fort Lauderdale's Intracoastal Waterway",
        caption: "The Intracoastal Waterway — the parade's long northern stretch",
      },
      {
        src: "photo-1705351901492-34c189b1ae1b",
        alt: "Boats on the water in Fort Lauderdale",
        caption: "Boats gather along the route from early evening",
      },
      {
        src: "photo-1590202819955-8aff5f185180",
        alt: "Aerial view of Fort Lauderdale's waterfront and skyline",
        caption: "Downtown Fort Lauderdale — where the parade steps off",
      },
    ],
    stayNote:
      "Every home is 10–15 minutes from the mid-route viewing areas with free parking — an easy rideshare on parade night. Groups pick the Private Pool Oasis (sleeps six, heated pool); couples pick the king suites a short walk from Wilton Drive; the 2BR efficiencies sleep four.",
    atGlance: {
      h: "Winterfest Boat Parade {year} at a Glance",
      items: [
        { icon: "calendar", text: "{dates}" },
        { icon: "pin", text: "Fort Lauderdale — New River & Intracoastal Waterway" },
        { icon: "clock", text: "Evening event — step-off typically around 6:30pm" },
        { icon: "ticket", text: "Public viewing is free" },
        { icon: "car", text: "Parking can be challenging — arrive early, or rideshare" },
        { icon: "house", text: "Double Rainbow Suites: about 10–15 minutes from the viewing areas" },
      ],
    },
    route: {
      h: "The parade route",
      paragraphs: [
        "The parade lines up on the New River in downtown Fort Lauderdale and steps off in the early evening — typically around 6:30pm — a slow, glowing procession of yachts, showboats and anything that floats.",
        "From downtown it cruises east along the New River past the Las Olas riverfront, then turns north up the Intracoastal Waterway, passing waterfront restaurants and the beachside skyline before finishing at Lake Santa Barbara in Pompano Beach — about 12 miles end to end.",
        "Timing depends on where you watch: downtown sees the first boats early, while the northern stretches around Oakland Park and Commercial Boulevards run an hour or two later. The whole procession takes roughly two hours to pass any given point.",
      ],
    },
    viewingSpots: {
      h: "Best places to watch",
      items: [
        "Las Olas riverfront & the Riverwalk (downtown) — the liveliest free viewing, right at the start of the route; arrive a couple of hours early.",
        "Esplanade Park & Huizenga Plaza — free riverside parks downtown with room for chairs and a cooler.",
        "The Intracoastal through northeast Fort Lauderdale — thinner crowds and easier parking along the later, northern part of the route.",
        "Waterfront restaurants along the route — many run watch parties with reserved seating; book well ahead.",
        "Official grandstand seating — ticketed through the Winterfest site for guaranteed front-row views.",
      ],
    },
    gettingThere: [
      "Driving in for the night: downtown garages and the lots around Las Olas fill by mid-afternoon, and drawbridges along the route open for the procession — arrive early and plan your crossings. Coming from elsewhere in South Florida, Brightline's Fort Lauderdale station is a short walk from the riverfront viewing.",
      "The Water Taxi runs along the parade route and is a festive way in; rideshare works well too, but expect surge pricing when the last boats pass.",
      "From our homes it's 10–15 minutes to the mid-route viewing areas. Our guests' move: rideshare in for the boats, then spend the rest of the weekend car-free on Wilton Drive.",
    ],
    restaurants: {
      h: "Restaurants near the route",
      items: [
        "15th Street Fisheries — classic dockside seafood right on the Intracoastal route.",
        "Shooters Waterfront — a big Intracoastal terrace made for parade night.",
        "Blue Moon Fish Co. — Intracoastal-front dining on the northern stretch.",
        "Rivertail & The Wharf — lively New River spots downtown, near the start of the route.",
        "Book weeks ahead for parade night — or keep it simple and eat on Wilton Drive, a short walk from your suite.",
      ],
    },
    whyStay: {
      h: "Why stay in Wilton Manors",
      text: "Wilton Manors puts you 10–15 minutes from the best mid-route viewing while dodging the night's two headaches: surge-priced beachfront hotels and bridge-closure gridlock. Park free at your home, hop a quick rideshare to the water, and spend the rest of the weekend on foot — Wilton Drive's restaurants, bars and brunches are a short walk from every Double Rainbow Suites home. It's the relaxed local base for the busiest boat night of the year.",
    },
    weekendExtras: {
      h: "Make a weekend of it",
      items: [
        "Friday: dinner and a wander down Wilton Drive — the main street was made for it.",
        "Saturday daytime: Fort Lauderdale Beach and the promenade before the evening parade.",
        "Sunday: a long brunch on the Drive — Rosie's is the local institution — then Las Olas Boulevard or Hugh Taylor Birch State Park.",
        "Any evening: ride the Water Taxi along the very waterway the parade sailed.",
        "December is the locals' favorite month — warm, dry days and everything lit up for the holidays.",
      ],
    },
    knowBefore: [
      "The parade starts in the early evening and takes a couple of hours to pass any given point — bring chairs and a cooler.",
      "December evenings can be breezy on the water — pack a light layer.",
      "Grandstand seating is ticketed; the rest of the route is free to watch.",
      "Drawbridges on the route open for the procession — if you're driving, cross before the parade reaches you.",
      "It's one of the busiest nights of the year on the water — and one of the most magical.",
    ],
    hostTip:
      "Make a weekend of it: parade on Saturday night, brunch on Wilton Drive on Sunday. December is our favorite month here — warm days, no humidity, everything lit up.",
    faqs: [
      {
        q: "When is the Winterfest Boat Parade {year}?",
        a: "The {year} parade sails on {dates}, stepping off in the early evening — typically around 6:30pm — from the New River in downtown Fort Lauderdale.",
      },
      {
        q: "Is the Winterfest Boat Parade the same as Fort Lauderdale's Christmas Boat Parade?",
        a: "Yes — same parade, two names. It's officially billed as the Winterfest Boat Parade, but plenty of people know it as Fort Lauderdale's Christmas boat parade: the December night parade of illuminated boats on the New River and Intracoastal.",
      },
      {
        q: "What is the Winterfest Boat Parade route?",
        a: "About 12 miles: from the New River downtown, east past the Las Olas riverfront, then north up the Intracoastal Waterway to Lake Santa Barbara in Pompano Beach. Downtown sees the first boats early; the northern stretch runs an hour or two later.",
      },
      {
        q: "Where is the best place to watch the Winterfest Boat Parade?",
        a: "Free viewing lines the New River and Intracoastal — the Las Olas riverfront and waterfront parks are favorites. Arrive a couple of hours early, or book a waterfront restaurant watch party.",
      },
      {
        q: "Is the Winterfest Boat Parade free?",
        a: "Watching from the public waterfront is free; grandstand seating is ticketed through the official site.",
      },
      {
        q: "Where can I park for the Winterfest Boat Parade?",
        a: "Downtown municipal garages and the Las Olas lots are closest and fill by mid-afternoon. Skipping the car is easier: Brightline stops a short walk from the riverfront, the Water Taxi runs the route, and our guests rideshare from Wilton Manors in 10–15 minutes.",
      },
      {
        q: "What restaurants are near the Winterfest Boat Parade route?",
        a: "Right on the water along the route: 15th Street Fisheries, Shooters Waterfront and Blue Moon Fish Co. on the Intracoastal, and Rivertail and The Wharf on the New River downtown. Reserve well ahead for parade night — or dine on Wilton Drive, a short walk from our homes.",
      },
      {
        q: "Where should I stay for the Winterfest Boat Parade?",
        a: "Wilton Manors, 10–15 minutes from the mid-route viewing: free parking at your home, a quick rideshare to the water and a walkable dining strip for the rest of the weekend. Our pet-friendly homes book out early for parade weekend.",
      },
      {
        q: "Why stay in Wilton Manors for Winterfest?",
        a: "You're close to the route without beachfront-hotel prices or bridge gridlock — and the rest of the weekend is walkable, with Wilton Drive's restaurants and bars on your doorstep.",
      },
      {
        q: "How far is Double Rainbow Suites from the parade route?",
        a: "About 10–15 minutes by car or rideshare to the best mid-route viewing areas. Allow a little extra on parade night itself — it's the busiest boat night of the year.",
      },
      {
        q: "Which Double Rainbow Suites home is best for Winterfest?",
        a: "Bringing the crew? The Private Pool Oasis sleeps six with its own heated pool. Couples love the two Wilton Manors king suites a short walk from the Drive, and the spacious 2BR efficiencies sleep four.",
      },
      {
        q: "What else is there to do in Fort Lauderdale that weekend?",
        a: "Make it a full winter escape: Fort Lauderdale Beach and the promenade, Las Olas Boulevard, Hugh Taylor Birch State Park, a Water Taxi ride along the parade waterway, and Wilton Drive's restaurants and famous drag brunch.",
      },
    ],
    officialUrl: "https://www.winterfestparade.com/",
  },
  {
    slug: "wicked-manors-halloween",
    ogImage: "/og/events/wicked-manors-halloween.jpg",
    eventId: "wicked-manors",
    eventName: "Wicked Manors Halloween on Wilton Drive",
    shortTitle: "Wicked Manors Halloween Guide",
    category: "Halloween · Wilton Manors",
    h1: "Wicked Manors {year} — South Florida's Wildest Halloween Night",
    metaTitle: "Wicked Manors Halloween {year} — Wilton Drive Party & Stays",
    metaDescription:
      "Wicked Manors hits Wilton Drive on {dates}: South Florida's wildest Halloween street party — free to attend, spectacular costumes — with walk-home suites just off the Drive.",
    answer:
      "Every October 31, Wilton Drive shuts down for Wicked Manors — one of the largest Halloween street festivals in Florida, and the most spectacular night of the year on the Drive. Tens of thousands of revellers fill the street in elaborate costumes for bars, pop-up stages and outdoor parties along the full strip. Double Rainbow Suites' Wilton Manors homes are a short walk from it all — go as hard as you want and walk home.",
    fallback: { start: "2026-10-31", end: "2026-10-31", checkout: "2026-11-01", label: "October 31, 2026", year: "2026" },
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    stayNote:
      "All three Wilton Manors homes are a short walk from Wilton Drive — the whole point on Halloween night; the Private Pool Oasis is about a 5-minute drive.",
    gettingThere: [
      "Parking near Wilton Drive on Halloween night is nearly impossible — our guests have the best deal in town: leave the car at the house and walk. Our properties sit in the residential streets just behind the main strip.",
      "Rideshares are plentiful earlier in the evening but slow after midnight — walking back is the move. Coming from elsewhere, arrive before the road closures and expect a short walk from the outer lots.",
    ],
    knowBefore: [
      "The Drive closes to traffic from early evening and every venue's outdoor space fills fast.",
      "Costumes range from clever to jaw-dropping — this crowd takes Halloween seriously.",
      "The street festival is free; individual venues may run ticketed parties inside.",
      "The street peaks around 10–11pm — arrive early for the full spectacle, later for peak energy.",
      "Halloween weekend is one of our busiest of the year — book four to six weeks out.",
    ],
    hostTip:
      "Agree a meeting point with your crew before you hit the Drive — it gets wall-to-wall and phone signal gets patchy. And pick a costume with comfortable shoes; you'll be on your feet for hours.",
    faqs: [
      {
        q: "When is Wicked Manors {year}?",
        a: "Wicked Manors takes over Wilton Drive every October 31 — next up, {dates}. The street festival runs from early evening into the early hours.",
      },
      {
        q: "What is Wicked Manors?",
        a: "Wicked Manors is an annual outdoor Halloween street festival on Wilton Drive in Wilton Manors, Florida — one of the largest Halloween events in South Florida, drawing tens of thousands of costumed revellers to the LGBTQ+ heart of Fort Lauderdale every October 31.",
      },
      {
        q: "Is Wicked Manors LGBTQ+ friendly?",
        a: "Completely. Wilton Manors is one of the most LGBTQ+ welcoming cities in the United States, and Wicked Manors reflects that — all are welcome, all costumes celebrated.",
      },
      {
        q: "Do I need tickets for Wicked Manors?",
        a: "The street festival itself is free to attend. Individual venues may have covers or ticketed events — check with specific bars and clubs for details.",
      },
      {
        q: "How close are Double Rainbow Suites to Wilton Drive?",
        a: "Our king suites on NE 24th Street are a short walk to Wilton Drive — close enough to walk home at the end of the night.",
      },
    ],
    officialUrl: "https://pridecenterflorida.org/",
  },
  {
    slug: "pig-week-fort-lauderdale",
    ogImage: "/og/events/pig-week-fort-lauderdale.jpg",
    eventId: "pig-week",
    eventName: "Pig Week Fort Lauderdale",
    shortTitle: "Pig Week Fort Lauderdale Guide",
    category: "Leather & fetish festival · Wilton Manors",
    h1: "Pig Week Fort Lauderdale {year} — Stay in the Heart of Wilton Manors",
    metaTitle: "Pig Week Fort Lauderdale {year} — Dates, Venues & Stays",
    metaDescription:
      "Pig Week {dates}: nearly 60 events at Ramrod, The Manor and across Wilton Manors — registration tips and the Psycho Circus main party, plus private suites a short walk from the Drive.",
    answer:
      "Pig Week is Fort Lauderdale's premier leather, kink and fetish festival — ten days of parties, socials, workshops and events across the bars, clubs, pools and private venues of Wilton Manors, running Thanksgiving weekend through the first week of December. It's the world's largest event of its kind, drawing thousands of attendees from more than 40 countries. Double Rainbow Suites puts you a short walk from the Drive and all the action, with private accommodation that's all yours for the week.",
    fallback: { start: "2026-11-27", end: "2026-12-06", checkout: "2026-12-07", label: "November 27 – December 6, 2026", year: "2026" },
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    stayNote:
      "Our king suites sleep two each in fully private, self-contained space — your own retreat between events, away from the guest-house scene. The 2-bedroom efficiency sleeps four.",
    gettingThere: [
      "Pig Week spreads across the venues of Wilton Manors — Ramrod, The Manor, The Eagle and dozens more — nearly all on or just off Wilton Drive, a short walk from our homes.",
      "Registration (the dog tag that gets you discounted or free entry to most events) opens at Ramrod in the days before the festival. For anything further afield, rideshare is easiest.",
    ],
    knowBefore: [
      "Pig Week is adults-only (18+), drawing a sex-positive leather, kink and fetish community from 40+ countries.",
      "Nearly 60 events run across ten days, afternoon through the early hours — pace yourself.",
      "The main party is the Psycho Circus at The Manor.",
      "Registration is optional but worth it — the dog tag brings free or discounted entry plus local business discounts all week.",
      "Dates run Thanksgiving through early December — flights and rooms book out months ahead.",
    ],
    hostTip:
      "Your own kitchen between events is the underrated move of the week — and register early at Ramrod; the dog tag pays for itself in a couple of nights.",
    faqs: [
      {
        q: "When is Pig Week {year}?",
        a: "Pig Week runs {dates} — Thanksgiving weekend through the first week of December. Dates for future years are typically announced in late summer at pigweek.com.",
      },
      {
        q: "Is Pig Week adults only?",
        a: "Yes. Pig Week is an adults-only (18+) event. All events are for adult attendees only.",
      },
      {
        q: "How far are Double Rainbow Suites from the Pig Week venues?",
        a: "Our homes sit in the residential streets just off Wilton Drive in Wilton Manors — a short walk to Ramrod, The Manor, The Eagle and the other main venues. Rideshare is best for anything further afield.",
      },
      {
        q: "Do I need to register for Pig Week?",
        a: "Registration (dog tags) is optional but recommended — it gets you free or discounted entry to most events plus some swag. You can attend without registering and pay at the door. Registration opens at Ramrod in the days before the festival.",
      },
    ],
    officialUrl: "https://www.pigweek.com/",
  },
  {
    slug: "florida-leather-week",
    ogImage: "/og/events/florida-leather-week.jpg",
    eventId: "florida-leather-week",
    eventName: "Florida Leather Week",
    shortTitle: "Florida Leather Week Guide",
    category: "Leather & fetish week · Wilton Manors",
    h1: "Florida Leather Week {year} — Stay Steps From the Eagle",
    metaTitle: "Florida Leather Week {year} — Dates, Venues & Stays",
    metaDescription:
      "Florida Leather Week {dates}: nine days of bar takeovers, the Mr. Eagle competition and the Boundless Block Party across Wilton Manors — plus private king suites a short walk from the Eagle.",
    answer:
      "Florida Leather Week is a week built by and for the leather and fetish community in Wilton Manors and Fort Lauderdale — nine days of bar takeovers, competitions, socials and parties centered on the Eagle Wilton Manors and Ramrod. Highlights include the free opening Leather Pride Walk and flag raising, the Mr. Eagle Wilton Manors competition, and the Boundless Block Party on NE 26th St. Double Rainbow Suites' homes at 1425 NE 24th St put you a short walk from the Eagle and Wilton Drive — and about two minutes from the block party.",
    fallback: { start: "2026-11-07", end: "2026-11-15", checkout: "2026-11-16", label: "November 7 – 15, 2026", year: "2026" },
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    stayNote:
      "Our king suites sleep two each in fully private, self-contained space — your own retreat between events, away from the guest-house scene. The 2-bedroom efficiency sleeps four, and the Boundless Block Party on NE 26th St is a two-minute walk from your door.",
    gettingThere: [
      "Most events center on the Eagle Wilton Manors on Wilton Drive — a short walk from our homes. The opening Leather Pride Walk sets off from Richardson Park and ends at the Eagle.",
      "The Boundless Block Party takes over NE 26th St, around the corner from our suites, and the closing party is at Ramrod in Fort Lauderdale — a quick rideshare. For anything further afield, rideshare is easiest.",
    ],
    knowBefore: [
      "Florida Leather Week is adults-only — 18+ for most events, 21+ where alcohol is served. Bring a valid government-issued photo ID.",
      "Nine days of events run afternoon through the early hours — pace yourself.",
      "Leather, uniform and fetish gear are encouraged; street-legal attire is always welcome.",
      "Plenty of events are free — the Pride Walk, the Mr. Eagle competition and the closing party. Ticketed parties start around $12, and the Chili Cook Off is a $15 donation.",
      "November is the start of high season — flights and rooms book out early.",
    ],
    hostTip:
      "Walk to everything — the Eagle, the block party and the Drive are all on foot from the suites. Don't miss the opening Pride Walk on the first Saturday: it's free, it ends at the Eagle, and it sets the tone for the whole week.",
    faqs: [
      {
        q: "When is Florida Leather Week {year}?",
        a: "Florida Leather Week runs {dates}. Dates for future years are announced at floridaleatherweek.com.",
      },
      {
        q: "Is Florida Leather Week adults only?",
        a: "Yes — events are 18+ (21+ where alcohol is served), and a valid government-issued photo ID is required for entry.",
      },
      {
        q: "Do I need to wear leather to attend?",
        a: "No — leather, uniform and fetish gear are encouraged but never required. Street-legal attire is welcome at every event.",
      },
      {
        q: "How far are Double Rainbow Suites from the Leather Week venues?",
        a: "Our homes at 1425 NE 24th St are a short walk from the Eagle Wilton Manors and Wilton Drive, and about two minutes on foot from the Boundless Block Party on NE 26th St. The closing party at Ramrod is a quick rideshare away.",
      },
    ],
    officialUrl: "https://www.floridaleatherweek.com/",
  },
  {
    slug: "white-party-fort-lauderdale",
    ogImage: "/og/events/white-party-fort-lauderdale.jpg",
    eventId: "white-party",
    eventName: "White Party Fort Lauderdale",
    shortTitle: "White Party Fort Lauderdale Guide",
    category: "Circuit party weekend · Wilton Manors",
    h1: "White Party Fort Lauderdale {year} — Memorial Day Weekend on Wilton Drive",
    metaTitle: "White Party Fort Lauderdale {year} — Memorial Day Weekend Guide",
    metaDescription:
      "White Party Fort Lauderdale, {dates}: Memorial Day weekend's iconic all-white circuit party at The Manor — tickets, pre-parties and walk-home suites just off Wilton Drive.",
    answer:
      "The White Party Fort Lauderdale is one of the most iconic circuit-party weekends in the LGBTQ+ calendar — an annual Memorial Day Weekend celebration that has grown from a single night into a full festival of parties across Wilton Manors. The main event takes over The Manor complex on Wilton Drive with a red carpet, world-class DJs, drag stars, cirque acts and a sea of white. Double Rainbow Suites sits just off the Drive — close enough to walk home, far enough to sleep in.",
    fallback: { start: "2027-05-28", end: "2027-05-31", checkout: "2027-06-01", label: "May 28–31, 2027", year: "2027" },
    propertySlugs: ["wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies", "pet-friendly-pool-oasis"],
    stayNote:
      "The Manor and the rest of the Drive are a short walk from our king suites — no 3am rideshare coordination required. Every home has free off-street parking.",
    gettingThere: [
      "The signature event takes over The Manor complex on Wilton Drive, with pre-parties, pool parties and satellite events across the Drive and Fort Lauderdale through the weekend.",
      "Staying with us, you walk — the whole point. Flying in, FLL is 15–20 minutes away; driving in from Miami or elsewhere in South Florida, park free at your home and forget the car.",
    ],
    knowBefore: [
      "White is the dress code — not optional. From white shorts and tanks to full couture, the all-white aesthetic is the spectacle.",
      "The weekend runs Friday to Monday; the main red-carpet event lands Saturday or Sunday night.",
      "Sunday afternoon pool parties are a fixture; Monday is beach-recovery day.",
      "The crowd is international — New York, Miami, Latin America and Europe all fly in.",
      "Memorial Day weekend books out months ahead — three to six months out is standard.",
    ],
    hostTip:
      "Pack your white look in your carry-on — lost luggage is the classic White Party disaster. And don't sleep through Sunday's pool parties; they're the sleeper highlight of the weekend.",
    faqs: [
      {
        q: "When is White Party Fort Lauderdale {year}?",
        a: "The White Party lands every Memorial Day weekend — next up, {dates}. Confirm the exact program and tickets at garysantisevents.com.",
      },
      {
        q: "Is there a dress code for the White Party?",
        a: "Yes — all-white outfits are required for the main event. Pre-parties and satellite events may be more relaxed; check individual event listings.",
      },
      {
        q: "How far are Double Rainbow Suites from The Manor?",
        a: "The Manor is on Wilton Drive, a short walk from our king suites on NE 24th Street in Wilton Manors. No rideshare needed.",
      },
      {
        q: "How early should I book for White Party weekend?",
        a: "As early as possible — Memorial Day weekend in Wilton Manors is one of the most in-demand periods of the year. Booking three to six months out is standard for White Party attendees.",
      },
    ],
    officialUrl: "https://garysantisevents.com/",
  },
  {
    slug: "las-olas-art-fair",
    ogImage: "/og/events/las-olas-art-fair.jpg",
    eventId: "las-olas-art-fair-jan",
    eventIds: ["las-olas-art-fair-jan", "las-olas-art-fair-mar"],
    eventName: "Las Olas Art Fair",
    shortTitle: "Las Olas Art Fair Guide",
    category: "Arts · Las Olas Boulevard",
    h1: "Las Olas Art Fair {year} — Dates, Parking & Where to Stay",
    metaTitle: "Las Olas Art Fair {year} — Dates, Parking & Where to Stay",
    metaDescription:
      "Las Olas Art Fair {dates}: what to see, parking and Water Taxi tips, the best boulevard restaurants and where to stay — a local guide from hosts 15 minutes away.",
    answer:
      "The Las Olas Art Fair turns Fort Lauderdale's most famous boulevard into a free open-air gallery twice a year — January and March — with hundreds of juried artists lining the palm-shaded street between the cafés and boutiques. Double Rainbow Suites' pet-friendly homes are about 15 minutes away, with free parking and Wilton Drive's restaurants for the evenings.",
    fallback: { start: "2027-01-02", end: "2027-01-03", checkout: "2027-01-04", label: "January 2–3, 2027", year: "2027" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    photos: [
      {
        src: "photo-1590202819955-8aff5f185180",
        alt: "Aerial view of downtown Fort Lauderdale and the New River",
        caption: "Las Olas Boulevard runs from downtown all the way to the beach",
      },
      {
        src: "photo-1503198383638-2199ff0637a3",
        alt: "Aerial view of boats along Fort Lauderdale's waterways",
        caption: "Arrive festive: the Water Taxi stops right on Las Olas",
      },
      {
        src: "photo-1484199383121-dfa3c30608cd",
        alt: "Aerial view of Fort Lauderdale Beach",
        caption: "The Atlantic waits at the east end of the boulevard",
      },
    ],
    stayNote:
      "All four homes are about 15 minutes from the boulevard with free parking — and back home, Wilton Drive's restaurants and bars make the evening half of the day.",
    route: {
      h: "The fair layout",
      paragraphs: [
        "The fair closes the central stretch of Las Olas Boulevard to traffic and fills it with artists' booths on both sides — fine art, sculpture, photography, jewelry and ceramics, all juried, all free to browse.",
        "It runs both Saturday and Sunday, typically mid-morning to late afternoon — and there are two editions each year, January (Part I) and March (Part II), with different artists at each.",
        "Make a day of the neighborhood: the fair sits between the Riverwalk and the beach, with the NSU Art Museum and the boulevard's cafés and galleries as permanent neighbors.",
      ],
    },
    viewingSpots: {
      h: "Fair day tips",
      items: [
        "Arrive near opening — the best parking and the first pick of the art both go early.",
        "Walk the full length before buying; mark your favorites and circle back.",
        "The artists are right there — commissions and custom work are part of the fun.",
        "Lunch on the boulevard is half the experience; the café patios fill by noon.",
        "Sunday afternoons run quieter — better conversation, occasionally better prices.",
      ],
    },
    gettingThere: [
      "Parking on and around Las Olas fills by late morning on fair days — the municipal garages a block or two off the boulevard are the reliable play, and the Water Taxi's Las Olas stop turns the arrival into a sightseeing ride.",
      "From our homes it's about 15 minutes by car. Our guests' move: arrive near opening, browse before the crowds, long lunch on the boulevard, home for a pool float.",
      "Coming from Miami or West Palm Beach, Brightline to downtown Fort Lauderdale leaves you a short rideshare — or a pleasant stroll down the boulevard — from the fair.",
    ],
    restaurants: {
      h: "Eating on the boulevard",
      items: [
        "Louie Bossi's — the boulevard's beloved Italian; the patio is the fair-weekend seat to beat.",
        "Rocco's Tacos — loud, fun and steps from the booths.",
        "El Camino — stylish Mexican with a proper mezcal list.",
        "Boathouse at the Riverside — New River views at the west end of the fair.",
        "Our pick: dinner back on Wilton Drive — 15 minutes away, parking included.",
      ],
    },
    whyStay: {
      h: "Why stay in Wilton Manors",
      text: "An art-fair weekend pairs naturally with Wilton Manors: browse the boulevard by day, then come home to free parking, a quiet street and a walkable row of restaurants for the evening. Our homes sit about 15 minutes from Las Olas — close enough to make two trips in a day if you spot something big on Saturday and want to think it over.",
    },
    weekendExtras: {
      h: "Make a weekend of it",
      items: [
        "Saturday: the fair by day, Wilton Drive's bars and restaurants by night.",
        "Sunday morning: the NSU Art Museum, a two-minute walk off the fair route.",
        "Sunday afternoon: the beach at the east end of the boulevard — toes in the Atlantic by four.",
        "Any evening: a Water Taxi ride along the New River and Intracoastal.",
      ],
    },
    knowBefore: [
      "Entry is free; the art ranges from affordable prints to serious collector pieces.",
      "There are two editions a year — January and March — with different artists at each.",
      "South Florida sun is real even in January — hat and sunscreen for the midday stretch.",
      "Most artists take cards, and big pieces can usually be shipped — ask before you carry.",
    ],
    hostTip:
      "We tell guests: browse Saturday, buy Sunday morning, brunch on the Drive after. And if a piece won't fit in the car — talk to the artist. Shipping art is their daily business.",
    faqs: [
      {
        q: "When is the Las Olas Art Fair {year}?",
        a: "The fair runs twice a year, in January and March. The {year} January edition takes over Las Olas Boulevard on {dates}, Saturday and Sunday, with the March edition (Part II) following in spring.",
      },
      {
        q: "Is the Las Olas Art Fair free?",
        a: "Yes — strolling the fair is completely free. Bring a budget only if a piece of art follows you home.",
      },
      {
        q: "Where is the Las Olas Art Fair held?",
        a: "On the central stretch of Las Olas Boulevard in Fort Lauderdale, closed to traffic for the weekend — between the Riverwalk downtown and the beach.",
      },
      {
        q: "Where do I park for the Las Olas Art Fair?",
        a: "Boulevard parking fills by late morning — use the municipal garages a block or two off Las Olas, or arrive by Water Taxi. Our guests park free at their home and drive 15 minutes in.",
      },
      {
        q: "What restaurants are near the Las Olas Art Fair?",
        a: "The boulevard itself: Louie Bossi's, Rocco's Tacos and El Camino sit right among the booths, with Boathouse at the Riverside at the west end. For dinner, Wilton Drive is 15 minutes away.",
      },
      {
        q: "Where should I stay for the Las Olas Art Fair?",
        a: "About 15 minutes from the boulevard is the sweet spot — free parking, a kitchen, and Wilton Drive's restaurants for the evenings. Our pet-friendly homes sleep two to six.",
      },
      {
        q: "How far is Double Rainbow Suites from the art fair?",
        a: "About 15 minutes by car from all four of our homes — close enough to browse Saturday and return Sunday for the piece you couldn't stop thinking about.",
      },
      {
        q: "What else is there to do that weekend?",
        a: "The NSU Art Museum sits just off the fair route, the Riverwalk and Water Taxi are right there, the beach ends the boulevard, and Wilton Drive's brunch and nightlife are a short walk from our suites.",
      },
    ],
    officialUrl: "https://www.artfestival.com/festivals/las-olas-art-fair-part-i",
  },
  {
    slug: "st-patricks-parade-fort-lauderdale",
    ogImage: "/og/events/st-patricks-parade-fort-lauderdale.jpg",
    eventId: "st-patricks",
    eventName: "Fort Lauderdale St. Patrick's Parade & Festival",
    shortTitle: "St. Patrick's Parade Guide",
    category: "Festival · Downtown Fort Lauderdale",
    h1: "Fort Lauderdale St. Patrick's Parade {year} — Route, Parking & Where to Stay",
    metaTitle: "Fort Lauderdale St. Patrick's Parade {year} — Route, Parking & Where to Stay",
    metaDescription:
      "Fort Lauderdale St. Patrick's Parade {dates}: the downtown route, Himmarshee street festival, parking and Brightline tips — plus where to stay, from hosts 15 minutes away.",
    answer:
      "Downtown Fort Lauderdale goes green on {dates}: the St. Patrick's Parade marches through the city center, followed by a free street festival of Irish music, food and dancing in the Himmarshee entertainment district. Double Rainbow Suites' pet-friendly homes are about 15 minutes away — an easy rideshare in, a quiet night's sleep after.",
    fallback: { start: "2027-03-13", end: "2027-03-13", checkout: "2027-03-14", label: "March 13, 2027", year: "2027" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    photos: [
      {
        src: "photo-1590202819955-8aff5f185180",
        alt: "Aerial view of downtown Fort Lauderdale, where the parade marches",
        caption: "The parade marches through the heart of downtown",
      },
      {
        src: "photo-1503198383638-2199ff0637a3",
        alt: "Fort Lauderdale's New River waterfront near the festival district",
        caption: "The Riverwalk and Himmarshee district host the after-festival",
      },
      {
        src: "photo-1705351901492-34c189b1ae1b",
        alt: "Boats on Fort Lauderdale's waterways at dusk",
        caption: "Arrive festive: the Water Taxi serves downtown",
      },
    ],
    stayNote:
      "All four homes are about 15 minutes from downtown with free parking — rideshare in for the green, home to a quiet street after.",
    route: {
      h: "The parade & festival layout",
      paragraphs: [
        "The parade traditionally marches through downtown along Las Olas Boulevard, with bagpipes, floats, Irish dancers and a city's worth of green — free to watch from the sidewalks the whole way.",
        "The day continues at the street festival in the Himmarshee entertainment district: live Irish music, food stalls, dancing and a sea of green well into the evening.",
        "It's family-friendly through the afternoon and livelier as the evening lands — pick your pace, both are the same event.",
      ],
    },
    viewingSpots: {
      h: "Best places to watch",
      items: [
        "The Las Olas sidewalks near the riverfront — the classic vantage, arrive early.",
        "A café patio on the boulevard — claim a table before the parade and keep it.",
        "Near the Himmarshee end — the parade's energy peaks as it reaches the festival.",
        "With little ones: the earlier stretches are calmer, and the festival is family-friendly by day.",
      ],
    },
    gettingThere: [
      "Brightline is the secret weapon: the downtown Fort Lauderdale station is a short walk from the parade route — the smartest arrival from Miami, West Palm Beach or Orlando.",
      "Driving: downtown garages fill by mid-morning on parade day. Rideshare is easy on the way in; expect a wait when the festival winds down.",
      "From our homes it's about 15 minutes by car or rideshare — close enough to nip home and change before the evening session.",
    ],
    restaurants: {
      h: "Where to eat & drink",
      items: [
        "Himmarshee's bars and kitchens — the festival is on their doorstep, and they know it.",
        "Louie Bossi's — the boulevard favorite, a short walk from the route.",
        "Rocco's Tacos — margaritas and momentum, right on Las Olas.",
        "Riverside Market — the locals' craft-beer institution, a few minutes away in Sailboat Bend.",
        "Our pick: dinner back on Wilton Drive — 15 minutes away, and Saturday night done properly.",
      ],
    },
    whyStay: {
      h: "Why stay in Wilton Manors",
      text: "Downtown on parade day is brilliant — and loud, and gridlocked, and priced accordingly. Our homes sit 15 minutes away in Wilton Manors: park free at your door, rideshare in for the green, and come home to a quiet street with Wilton Drive's restaurants a short walk away. Best of both, none of the parking.",
    },
    weekendExtras: {
      h: "Make a weekend of it",
      items: [
        "Friday: the Drive warms up for the weekend — dinner and a wander on Wilton Drive.",
        "Saturday: parade by day, festival into the evening, home in 15 minutes.",
        "Sunday: the recovery brunch — Rosie's on the Drive is the local institution.",
        "Sunday afternoon: the beach, the Water Taxi, or a float in the pool.",
      ],
    },
    knowBefore: [
      "The parade and street festival are free.",
      "Green isn't required — but it's noticeably universal.",
      "Family-friendly through the afternoon; the evening tilts adult in Himmarshee.",
      "March in Fort Lauderdale is peak spring — sunscreen even for a parade.",
    ],
    hostTip:
      "Do the parade and festival downtown, then come home to Wilton Manors for the evening — the Drive doesn't do shamrocks, but it does Saturday night better than anywhere.",
    faqs: [
      {
        q: "When is the Fort Lauderdale St. Patrick's Parade {year}?",
        a: "The {year} parade and festival take over downtown Fort Lauderdale on {dates} — parade first, then the Himmarshee street festival into the evening.",
      },
      {
        q: "What is the St. Patrick's Parade route in Fort Lauderdale?",
        a: "The parade traditionally marches through downtown along Las Olas Boulevard, ending near the Himmarshee entertainment district where the street festival runs.",
      },
      {
        q: "Is the Fort Lauderdale St. Patrick's Parade free?",
        a: "Yes — the parade and the street festival are both free to attend.",
      },
      {
        q: "Where do I park for the St. Patrick's Parade?",
        a: "Downtown garages fill by mid-morning. Brightline's downtown station is a short walk from the route, or rideshare — our guests park free at home and come in 15 minutes by car.",
      },
      {
        q: "What restaurants are near the parade?",
        a: "The Himmarshee district's bars and kitchens host the after-party; on the boulevard, Louie Bossi's and Rocco's Tacos are steps from the route. Wilton Drive is 15 minutes away for dinner.",
      },
      {
        q: "Where should I stay for the St. Patrick's Parade?",
        a: "About 15 minutes from downtown beats staying in the gridlock — our pet-friendly homes have free parking, kitchens and Wilton Drive's nightlife a short walk away.",
      },
      {
        q: "How far is Double Rainbow Suites from the parade?",
        a: "About 15 minutes by car or rideshare from all four homes — easy in, easy out, quiet night after.",
      },
      {
        q: "What else is there to do that weekend?",
        a: "The Riverwalk and Water Taxi downtown, Las Olas Boulevard, the beach ten minutes further, and Wilton Drive's famous Sunday drag brunch to finish.",
      },
    ],
    officialUrl: "https://www.stpatricksparade.com/",
  },
  {
    slug: "fort-lauderdale-july-4th-fireworks",
    ogImage: "/og/events/fort-lauderdale-july-4th-fireworks.jpg",
    eventId: "july-fourth",
    eventName: "Fort Lauderdale 4th of July Fireworks",
    shortTitle: "4th of July Fireworks Guide",
    category: "Holiday · Fort Lauderdale Beach",
    h1: "Fort Lauderdale 4th of July Fireworks {year} — Viewing, Parking & Where to Stay",
    metaTitle: "Fort Lauderdale 4th of July Fireworks {year} — Viewing, Parking & Where to Stay",
    metaDescription:
      "Fort Lauderdale 4th of July fireworks {dates}: best beach viewing spots, parking and bike tips, where to eat and where to stay — a local guide from hosts 10 minutes away.",
    answer:
      "Fort Lauderdale celebrates the 4th of July on the sand: a full beach day along the promenade capped by fireworks over the Atlantic after dark on {dates}. It's free to watch from anywhere on the beach — and Double Rainbow Suites' pet-friendly homes are about 10 minutes away, with complimentary beach-cruiser bikes that turn the parking crunch into a sunset ride.",
    fallback: { start: "2027-07-04", end: "2027-07-04", checkout: "2027-07-05", label: "July 4, 2027", year: "2027" },
    propertySlugs: ["pet-friendly-pool-oasis", "wilton-king-suite-north", "wilton-king-suite-south", "spacious-2br-efficiencies"],
    photos: [
      {
        src: "photo-1785510379885-eac1583e734e",
        alt: "Fireworks exploding over a dark beach at night",
        caption: "Fireworks over the Atlantic — the beach is the grandstand",
      },
      {
        src: "photo-1484199383121-dfa3c30608cd",
        alt: "Aerial view of Fort Lauderdale Beach on a summer day",
        caption: "Claim your spot on the sand early — it's a full beach day",
      },
      {
        src: "photo-1705351901492-34c189b1ae1b",
        alt: "Boats on the water at dusk in Fort Lauderdale",
        caption: "A beach day that ends in fireworks",
      },
    ],
    stayNote:
      "All four homes are about 10 minutes from the beach with free parking — and the complimentary cruiser bikes are the July 4th secret weapon. Groups pick the Private Pool Oasis (sleeps six); couples take the king suites; the 2BR efficiencies sleep four.",
    route: {
      h: "Where the fireworks happen",
      paragraphs: [
        "The fireworks launch over the ocean off Fort Lauderdale Beach, lighting up the whole promenade — there's no single 'right' spot, because the show is in the sky.",
        "The day itself is the event: the beach fills from morning, the strip hums all afternoon, and the sky show caps it after dark, typically around 9pm.",
        "In {year} the 4th falls on a Sunday — a natural three-day weekend on the sand.",
      ],
    },
    viewingSpots: {
      h: "Best places to watch",
      items: [
        "The sand near Las Olas & A1A — the heart of it, with the promenade behind you.",
        "North up the beach — the same sky with a fraction of the crowd.",
        "The promenade itself — stroll-and-watch, with the strip's bars and cafés in reach.",
        "From the water — boaters anchor off the beach; follow the official guidance on the day.",
      ],
    },
    gettingThere: [
      "Beach parking lots fill by mid-afternoon on the 4th — arrive by midday, or do what our guests do: take the complimentary beach-cruiser bikes, an easy 20 sunny minutes from our homes, and lock up by the sand.",
      "The Water Taxi plus a short walk is the festive option; rideshare works but surges hard after the finale — linger on the promenade for 30 minutes and let it pass.",
      "From our homes it's about 10 minutes by car. Honestly, on July 4th, take the bikes.",
    ],
    restaurants: {
      h: "Where to eat & drink",
      items: [
        "The A1A strip — beach cafés and bars built for the biggest beach day of the year.",
        "Casablanca Café — the historic house on A1A, close to the action.",
        "Coconuts — dockside seafood on the Intracoastal, a breezy stop on the way home.",
        "Pack a cooler — for plenty of locals, July 4th on the sand is a bring-your-own affair.",
        "Our pick: a late dinner on Wilton Drive after the fireworks — 10 minutes away, no beach exodus.",
      ],
    },
    whyStay: {
      h: "Why stay 10 minutes from the beach",
      text: "July 4th beach hotels charge holiday-peak rates for a day you'll spend on the public sand anyway. The smart setup is 10 minutes inland: free parking, a full kitchen for the cooler, complimentary bikes for the ride in — and for groups, a private heated pool for the day after. That's exactly what our four homes are.",
    },
    weekendExtras: {
      h: "Make a long weekend of it",
      items: [
        "The 4th falls on a Sunday in {year} — arrive Friday and claim the whole three days.",
        "Saturday: beach warm-up day, then dinner on Wilton Drive.",
        "Sunday: the 4th — beach all day, fireworks after dark, bikes home under the last of the sparks.",
        "Monday: the visitors fly home; the beach is yours. Sleep in, then go claim it.",
      ],
    },
    knowBefore: [
      "The fireworks are free to watch from anywhere on the public beach.",
      "July afternoons bring short storms — they pass; the show goes on after dark.",
      "Hydrate and reapply — July sun on reflective sand is no joke.",
      "Fireworks typically start around 9pm; the beach day itself starts much earlier.",
    ],
    hostTip:
      "Bikes beat everything on July 4th. Ride in, lock up by the sand, and you're back at the pool while the parking lots are still gridlocked.",
    faqs: [
      {
        q: "When are the Fort Lauderdale 4th of July fireworks {year}?",
        a: "On {dates} — the beach celebrates all day and the fireworks launch over the Atlantic after dark, typically around 9pm.",
      },
      {
        q: "Are the Fort Lauderdale 4th of July fireworks free?",
        a: "Yes — the fireworks are free to watch from anywhere along the public beach and promenade.",
        },
      {
        q: "Where is the best place to watch the 4th of July fireworks in Fort Lauderdale?",
        a: "The sand near Las Olas & A1A is the heart of it; north up the beach is the same sky with fewer people. Arrive early — it's a full beach day.",
      },
      {
        q: "Where do I park for the 4th of July fireworks?",
        a: "Beach lots fill by mid-afternoon. Our guests take the complimentary beach-cruiser bikes — about 20 easy minutes — or park west of the Intracoastal and Water Taxi across.",
      },
      {
        q: "What restaurants are near the fireworks?",
        a: "The A1A beach strip handles the crowds all day, Casablanca Café is the historic favorite, and Coconuts on the Intracoastal is an easy stop heading home. Or a late dinner on Wilton Drive, 10 minutes away.",
      },
      {
        q: "Where should I stay for the 4th of July in Fort Lauderdale?",
        a: "About 10 minutes from the beach — close enough to bike in, far enough to escape the holiday-peak hotel rates and gridlock. Our pet-friendly homes sleep two to six, with free parking and bikes.",
      },
      {
        q: "How far is Double Rainbow Suites from the fireworks?",
        a: "About 10 minutes by car, or roughly 20 minutes on the complimentary cruiser bikes — the July 4th secret weapon.",
      },
      {
        q: "What else is there to do on 4th of July weekend in Fort Lauderdale?",
        a: "The beach itself, the promenade, Water Taxi rides along the Intracoastal, and Wilton Drive's restaurants and famous Sunday drag brunch — all minutes from our homes.",
      },
    ],
    officialUrl: "https://www.visitlauderdale.com/",
  },
];

// Static venue/city per event for JSON-LD before (or without) the live fetch.
export const EVENT_PLACES = {
  "stonewall-pride": { venue: "Wilton Drive", city: "Wilton Manors" },
  flibs: { venue: "Bahia Mar, Fort Lauderdale Beach", city: "Fort Lauderdale" },
  tortuga: { venue: "Fort Lauderdale Beach Park", city: "Fort Lauderdale" },
  "air-show": { venue: "Fort Lauderdale Beach", city: "Fort Lauderdale" },
  winterfest: { venue: "New River & Intracoastal Waterway", city: "Fort Lauderdale" },
  "wicked-manors": { venue: "Wilton Drive", city: "Wilton Manors" },
  "pig-week": { venue: "Ramrod, The Manor & venues across Wilton Manors", city: "Wilton Manors" },
  "white-party": { venue: "The Manor, Wilton Drive", city: "Wilton Manors" },
  "las-olas-art-fair-jan": { venue: "Las Olas Boulevard", city: "Fort Lauderdale" },
  "st-patricks": { venue: "Himmarshee & Las Olas", city: "Fort Lauderdale" },
  "july-fourth": { venue: "Fort Lauderdale Beach", city: "Fort Lauderdale" },
};

export const getEventGuide = (slug) => eventGuides.find((g) => g.slug === slug);
export const guideForEvent = (eventId) =>
  eventGuides.find((g) => g.eventId === eventId || (g.eventIds || []).includes(eventId));
